import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { useLocation, useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { cloneDeep, debounce, keyBy, merge, values } from "lodash";
import qs from "query-string";

import {
  SearchBox,
  Pagination,
  HitsPerPage,
  connectStateResults,
} from "react-instantsearch-dom";

import {
  APPLICATION_TAB_NAMES,
  LOADING_STATES,
  MATCH_AGENT_MESSAGES,
  ROWS_PER_PAGE_ON_PAGINATED_TABLE,
  SEARCH_AGENT_MESSAGES,
  USER_LIST_VIEWS,
} from "lookup";
import {
  AuthContext,
  JobsContext,
  MatchContext,
  SearchContext,
} from "context/providers";
import { mapHits } from "utils/helpers/search";
import { mapMatchesToHit } from "utils/helpers/match";
import {
  createFilter,
  tabSupportsMatchAgentView,
  generateFilterChecksum,
  tabSupportsMatchScoreSort,
  tabSupportsTableView,
} from "./helpers/userCard";

import UserCardResult from "./components/UserCardResult";
import DownloadResults from "components/DownloadResults";
import TalentCreationModal from "components/TalentCreationModal";
import UserTableListView from "./components/UserTableListView";
import { Filters } from "./components";
import CustomToggle from "components/base/Toggle";
import WebAppliesLocationFilter from "./components/WebAppliesLocationFilter";
import { filterUsersByLocation } from "utils/helpers/locationFilter";
import { removeMatch } from "context/actions/match";
import { ListIcon, UserIcon, RotateCcwIcon, LoaderCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "components/ui/select";
import { SelectValue } from "@radix-ui/react-select";
import { useScrollToTopOnChange } from "hooks/useScrollToTopOnChange";
import PaginatedList from "./components/PaginatedList";
import { cn } from "utils";
import MatchAgentListView from "./components/MatchAgentListView";
import SvgIcon from "components/base/SvgIcon";
import DivLoader from "components/DivLoader";
import JobSearchAgent from "components/JobSearchAgent";
import {
  getUserListPreferences,
  normalizeUserListViewType,
  saveUserListPreferences,
} from "utils/helpers/userListPreferences";

const handleSearchingDebounce = debounce((setIsSearching, searching) => {
  setIsSearching(searching);
}, 250);

const getStatsText = ({
  reRankHitsForJob,
  reRankHitsForJobText,
  useMappedCount,
  resultsCount,
  plural,
  nbHits,
  hideProcessingTimeMS,
  maxProcessingTimeMs,
  processingTimeMS,
  downloadResults,
  displayedResultsCount,
  plusText,
}) => {
  if (reRankHitsForJob) {
    return reRankHitsForJobText;
  }

  if (useMappedCount) {
    return `${resultsCount.toLocaleString()} result${plural} found`;
  }

  if (!nbHits) {
    return "";
  }

  const processingTimeSuffix = hideProcessingTimeMS
    ? ""
    : ` in ${maxProcessingTimeMs ?? processingTimeMS}ms`;

  if (downloadResults) {
    const visibleResultsCount =
      displayedResultsCount ||
      Math.min(resultsCount, ROWS_PER_PAGE_ON_PAGINATED_TABLE);

    return `${visibleResultsCount.toLocaleString()} out of ${nbHits.toLocaleString()}${plusText} result${plural} found${processingTimeSuffix}.`;
  }

  return `${nbHits}${plusText} result${plural} found${processingTimeSuffix}`;
};

const CustomStats = connectStateResults(
  ({
    searchResults,
    isSearchStalled,
    finalItems,
    reRankHitsForJob,
    searching,
    hideProcessingTimeMS,
    downloadResults,
    downloadData,
    useFinalItemsCount,
  }) => {
    const [isSearching, setIsSearching] = useState(false);

    const hasSearchResults = !!searchResults;

    const {
      nbHits,
      totalRelation,
      nbSortedHits,
      areHitsSorted,
      processingTimeMS,
    } = searchResults ?? {};

    const rawResultsProcessingTimesMS = searchResults?._rawResults?.map(
      ({ processingTimeMS }) => processingTimeMS
    );

    // Instant search might trigger several requests this are handled in parallel by searchKit with open-search `_msearch`
    // instant search `searchResults.processingTimeMS` is the sum of the `processingTimeMS` of each request.
    // Since they are handled in parallel we are interested only in the max processingTimeMS.
    const maxProcessingTimeMs = rawResultsProcessingTimesMS
      ? Math.max(...rawResultsProcessingTimesMS)
      : undefined;

    useEffect(() => {
      handleSearchingDebounce(setIsSearching, isSearchStalled || searching);
    }, [isSearchStalled, searching]);

    const useMappedCount = useFinalItemsCount || !searchResults;
    const resultsCount = useMappedCount ? finalItems.length : nbHits ?? 0;
    const plural = `${resultsCount > 1 ? "s" : ""}`;
    const displayedResultsCount =
      finalItems?.length ?? ROWS_PER_PAGE_ON_PAGINATED_TABLE;

    const showingText =
      !useMappedCount && nbHits >= displayedResultsCount
        ? `${displayedResultsCount} out of `
        : "";

    const plusText = !useMappedCount && totalRelation === "gte" ? "+" : "";

    const reRankHitsForJobText = !hasSearchResults
      ? ""
      : areHitsSorted && nbHits !== nbSortedHits
      ? `${showingText}${
          nbSortedHits && nbSortedHits.toLocaleString()
        } relevant result${plural} sorted out of ${
          nbHits && nbHits.toLocaleString()
        }${plusText}`
      : `${showingText}${
          nbHits && nbHits.toLocaleString()
        }${plusText} result${plural} ${!showingText ? "found" : ""}`;

    const statsText = getStatsText({
      reRankHitsForJob,
      reRankHitsForJobText,
      useMappedCount,
      resultsCount,
      plural,
      nbHits,
      hideProcessingTimeMS,
      maxProcessingTimeMs,
      processingTimeMS,
      downloadResults,
      displayedResultsCount,
      plusText,
    });

    return (
      <div
        className={cn("ais-Stats flex flex-wrap items-center gap-x-2 gap-y-1", {
          "animate-pulse": isSearching && !useMappedCount,
        })}
      >
        {isSearching && !useMappedCount && !reRankHitsForJob && (
          <LoaderCircle
            strokeWidth={1}
            size={20}
            className="animate-spin opacity-50"
          />
        )}

        <span className="ais-Stats-text">{statsText}</span>

        {downloadResults && resultsCount > 0 && (
          <DownloadResults
            className="font-medium"
            data={downloadData}
            label="Download current page"
            hideIcon
            tooltip="Download current page"
          />
        )}
      </div>
    );
  }
);

const SearchGuard = connectStateResults(
  ({ searching, isSearchStalled, children, fallback, enabled }) => {
    if (enabled && (searching || isSearchStalled)) {
      return fallback;
    }
    return children;
  }
);

const DummyCard = () => (
  <div className="rounded-t-lg flex flex-wrap md:flex-nowrap border border-[#E5E7EB] p-4 box-border w-full gap-x-2 h-96 bg-white animate-pulse">
    <div>
      <div className="flex flex-col flex-none items-center h-full w-[125px]">
        <div className=" w-[125px]  h-[125px] bg-[#D9D9D9]" />
      </div>
    </div>

    <div className="w-full lg:w-[85%] md:w-3/4 xl:w-full">
      <div className="h-4 bg-slate-200 rounded w-48 my-4"></div>
      <div className="flex-1 space-y-6 py-1">
        <div className="h-2 bg-slate-200 rounded"></div>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-4">
            <div className="h-2 bg-slate-200 rounded col-span-2"></div>
            <div className="h-2 bg-slate-200 rounded col-span-1"></div>
          </div>
          <div className="h-2 bg-slate-200 rounded"></div>
        </div>
      </div>

      <div className="flex-1 space-y-6 py-1 mt-8">
        <div className="h-2 bg-slate-200 rounded"></div>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-4">
            <div className="h-2 bg-slate-200 rounded col-span-1"></div>
            <div className="h-2 bg-slate-200 rounded col-span-2"></div>
          </div>
          <div className="h-2 bg-slate-200 rounded"></div>
        </div>
      </div>

      <div className="flex-1 space-y-6 py-1 mt-8">
        <div className="h-2 bg-slate-200 rounded"></div>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-4">
            <div className="h-2 bg-slate-200 rounded col-span-1"></div>
            <div className="h-2 bg-slate-200 rounded col-span-2"></div>
          </div>
          <div className="h-2 bg-slate-200 rounded"></div>
        </div>
      </div>
    </div>
  </div>
);

const DummyHits = ({ message, renderMessage }) => {
  const messageClasses = cn("text-center text-red-400 text-lg mb-3");

  if (!!renderMessage) {
    return <div className={messageClasses}>{renderMessage}</div>;
  }

  return <div className={messageClasses}>{message}</div>;
};

const SORTBYKEY = {
  MATCH_RANK_SCORE: "Match Score",
  MATCHDATE: "Matched Date",
  LASTNAME: "Last name",
  RATE: "Rate",
};

let sortByKey = {};

const SORTBYDIRECTION = {
  ASCENDING: "Ascending",
  DESCENDING: "Descending",
};

const SORTBYKEYFIELD = {
  [SORTBYKEY.MATCH_RANK_SCORE]: "rankDetails.score",
  [SORTBYKEY.MATCHDATE]: "match.updatedAt",
  [SORTBYKEY.LASTNAME]: "family_name",
  [SORTBYKEY.RATE]: "ratePerHour.value",
};

let sortByKeyField = {};

const USER_LIST_VIEW_LABELS = {
  [USER_LIST_VIEWS.MATCH_AGENT]: "Career Track",
  [USER_LIST_VIEWS.FULL_PROFILE]: USER_LIST_VIEWS.FULL_PROFILE,
  [USER_LIST_VIEWS.LIST]: USER_LIST_VIEWS.LIST,
};

const CAREER_TRACK_ANALYSIS_TOOLTIP =
  "When this is enabled, we use our Match Agent to evaluate talent and re rank your search results. This may increase processing time";

function accessNestedProperty(obj, path) {
  if (!path) {
    return;
  }

  let keys = path.split(".");
  let current = obj;

  for (let key of keys) {
    if (!current[key]) {
      return;
    }
    current = current[key];
  }

  return current;
}

function sortComparator(a, b, field) {
  const valueA = accessNestedProperty(a, sortByKeyField[field]);
  const valueB = accessNestedProperty(b, sortByKeyField[field]);

  if (field === sortByKey.MATCHDATE) {
    return new Date(valueA) - new Date(valueB);
  }

  if (field === sortByKey.LASTNAME) {
    return valueA.localeCompare(valueB);
  }

  if (field === sortByKey.RATE) {
    return valueA - valueB;
  }

  if (field === sortByKey.MATCH_RANK_SCORE) {
    return valueA - valueB;
  }

  return 0;
}

const handleIsReRankingMatchesDebounce = debounce(
  (handleIsReRankingMatches, isLoading) => {
    handleIsReRankingMatches(isLoading);
  },
  650
);

const UserCardList = ({
  activeTabName,
  stats,
  search,
  pagination,
  downloadResults,
  collectionKey,
  noResultsMessage,
  renderCustomNoResults,
  showActions,
  excludedMatchStatuses,
  filterOutHitByMatchStatus,
  matchStatus,
  filter,
  allowSorting,
  showHitsPerPage,
  tabsMatchesRef,
}) => {
  const {
    matches,
    ideal,
    loadingIdeal,
    isReRankingMatches,
    mappedHitsTabMatchesForDownload,
    updateMappedTabHitsMatchesForDownload,
    handleIsReRankingMatches,
  } = useContext(MatchContext);
  const {
    hits,
    searchState,
    reRankHitsForJob,
    handleReRankHitsForJob,
    pageIsLoaded,
    handleReadyForSearch,
    loadingAISearch,
  } = useContext(SearchContext);
  const { user: loggedInUser } = useContext(AuthContext);

  const { jobOpp, reRankHitsForJobs, longList, loadJobLongList } =
    useContext(JobsContext);

  const [filters, setFilters] = useState({});

  const [items, setItems] = useState([]);
  const [searchAgentMessage, setSearchAgentMessage] = useState(0);
  const [matchAgentMessage, setMatchAgentMessage] = useState(0);

  const [locationFilterEnabled, setLocationFilterEnabled] = useState(false);
  const [locationFilterRadius, setLocationFilterRadius] = useState(50);

  const isHandlingReRankRef = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();
  const history = useHistory();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const QsStatusFilter = params.get("statusFilter");

  const isSearchResultsTab =
    activeTabName === APPLICATION_TAB_NAMES.SEARCHRESULTS ||
    activeTabName === "SEARCHRESULTS";

  const isLoadingAISearch = loadingAISearch === LOADING_STATES.LOADING;

  const paramViewType = params.get("viewType");

  const isMatchTabForMatchAgent =
    activeTabName === APPLICATION_TAB_NAMES.MATCHED ||
    activeTabName === APPLICATION_TAB_NAMES.SHORTLISTED ||
    activeTabName === APPLICATION_TAB_NAMES.LONGLISTED;

  const activeTabSupportsMatchAgentView =
    loggedInUser?.canAccessMatchAgent &&
    tabSupportsMatchAgentView(activeTabName);

  const activeTabSupportsTableView =
    loggedInUser?.canAccessListView && tabSupportsTableView(activeTabName);

  const [userListPreferences, setUserListPreferences] = useState(() =>
    getUserListPreferences(loggedInUser)
  );

  useEffect(() => {
    setUserListPreferences(getUserListPreferences(loggedInUser));
  }, [
    loggedInUser,
    loggedInUser?.id,
    loggedInUser?.username,
    loggedInUser?.email,
    loggedInUser?.attributes?.sub,
  ]);

  const careerTrackAnalysisEnabled =
    !!loggedInUser?.canAccessMatchAgent &&
    !!userListPreferences.careerTrackAnalysisEnabled;

  const currentViewType = normalizeUserListViewType(
    paramViewType ||
      userListPreferences.viewType ||
      USER_LIST_VIEWS.FULL_PROFILE
  );

  const viewTypeIsSupported = useCallback(
    (vt) => {
      if (vt === USER_LIST_VIEWS.MATCH_AGENT) {
        return activeTabSupportsMatchAgentView;
      }

      if (vt === USER_LIST_VIEWS.LIST) {
        return activeTabSupportsTableView;
      }

      return vt === USER_LIST_VIEWS.FULL_PROFILE;
    },
    [activeTabSupportsMatchAgentView, activeTabSupportsTableView]
  );
  const canDownloadSearchResults =
    downloadResults && loggedInUser?.canDownloadSearchResults;
  const canAccessTalentCreation =
    !!jobOpp?.id && loggedInUser?.canAccessResumeProfileTalentCreation;

  const [viewType, setViewType] = useState(
    viewTypeIsSupported(currentViewType)
      ? currentViewType
      : USER_LIST_VIEWS.FULL_PROFILE
  );

  sortByKey = { ...SORTBYKEY };

  sortByKeyField = { ...SORTBYKEYFIELD };

  const matchScoreSortEnabled =
    careerTrackAnalysisEnabled && tabSupportsMatchScoreSort(activeTabName);

  if (!matchScoreSortEnabled) {
    delete sortByKey.MATCH_RANK_SCORE;

    delete sortByKeyField[SORTBYKEY.MATCH_RANK_SCORE];
  }

  const [sortOrder, setSortOrder] = useState({
    key: matchScoreSortEnabled
      ? SORTBYKEY.MATCH_RANK_SCORE
      : SORTBYKEY.MATCHDATE,
    direction: SORTBYDIRECTION.DESCENDING,
  });

  useEffect(() => {
    const nextViewType = viewTypeIsSupported(currentViewType)
      ? currentViewType
      : USER_LIST_VIEWS.FULL_PROFILE;

    setViewType(nextViewType);
  }, [currentViewType, viewTypeIsSupported]);

  useEffect(() => {
    if (matchScoreSortEnabled && sortOrder.key !== SORTBYKEY.MATCH_RANK_SCORE) {
      setSortOrder({
        key: SORTBYKEY.MATCH_RANK_SCORE,
        direction: SORTBYDIRECTION.DESCENDING,
      });

      return;
    }

    if (
      !matchScoreSortEnabled &&
      sortOrder.key === SORTBYKEY.MATCH_RANK_SCORE
    ) {
      setSortOrder({
        key: SORTBYKEY.MATCHDATE,
        direction: SORTBYDIRECTION.DESCENDING,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchScoreSortEnabled, activeTabName]);

  const hasKeyStatusFilters = !!Object.keys(filters.status?.keys || {}).length;

  const handleQSStatusChange = () => {
    const paramsStatusFilter =
      QsStatusFilter?.split(",").filter((e) => e) || [];

    const finalFilters = JSON.parse(JSON.stringify(filters));

    Object.keys(finalFilters).forEach((outerKey) => {
      if (
        outerKey === "status" &&
        finalFilters[outerKey].hasOwnProperty("keys")
      ) {
        Object.keys(finalFilters[outerKey].keys).forEach((innerKey) => {
          const statusFilter = finalFilters[outerKey].keys[innerKey];

          statusFilter.value = paramsStatusFilter.includes(innerKey);
        });
      }
    });

    setFilters(finalFilters);
  };

  const cleanUpStatusFilterParams = () => {
    params.delete("statusFilter");
    const newUrl =
      location.pathname + (params.size > 0 ? `?${params.toString()}` : "");
    history.replace(newUrl);
  };

  useEffect(() => {
    if (hasKeyStatusFilters && QsStatusFilter) {
      handleQSStatusChange();
      cleanUpStatusFilterParams();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasKeyStatusFilters, QsStatusFilter]);

  const handleCareerTrackAnalysisChange = (enabled) => {
    const nextPreferences = {
      ...userListPreferences,
      careerTrackAnalysisEnabled: enabled,
    };

    setUserListPreferences(nextPreferences);
    saveUserListPreferences(loggedInUser, nextPreferences);

    if (enabled && jobOpp?.id && isSearchResultsTab) {
      handleReRankHitsForJob({
        id: jobOpp.id,
        isLoading: true,
      });
    } else if (!enabled) {
      handleReRankHitsForJob({
        id: "",
        isLoading: false,
      });
    }

    if (isSearchResultsTab) {
      handleReadyForSearch(true);
    }
  };

  const handleViewTypeChange = (vt, setQS = true, triggerSearch) => {
    const nextViewType = normalizeUserListViewType(vt);

    if (setQS) {
      const queryString = qs.stringify({
        ...qs.parse(location.search),
        viewType: nextViewType,
      });
      navigate(`${location.pathname}?${queryString}`, { replace: true });
    }

    const nextPreferences = {
      ...userListPreferences,
      viewType: nextViewType,
    };

    setUserListPreferences(nextPreferences);
    saveUserListPreferences(loggedInUser, nextPreferences);

    if (careerTrackAnalysisEnabled && jobOpp?.id && isSearchResultsTab) {
      handleReRankHitsForJob(jobOpp?.id, "id");
    } else if (!careerTrackAnalysisEnabled) {
      handleReRankHitsForJob({
        id: "",
        isLoading: false,
      });
    }

    if (triggerSearch) {
      handleReadyForSearch(true);
    }

    setViewType(nextViewType);
  };

  // Component Unmount
  useEffect(() => {
    return () => {
      handleReRankHitsForJob({
        id: "",
        isLoading: false,
      });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger Search when going to search results tab
  useEffect(() => {
    if (!searchState.basicSearch && jobOpp?.id && pageIsLoaded) {
      handleViewTypeChange(currentViewType, false, isSearchResultsTab);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabName]);

  useEffect(() => {
    if (currentViewType && !jobOpp?.id) {
      navigate(`${location.pathname}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewType, jobOpp?.id]);

  useEffect(() => {
    if (
      collectionKey === "longList" &&
      activeTabName === APPLICATION_TAB_NAMES.LONGLISTED &&
      jobOpp?.id &&
      longList.items.length === 0
    ) {
      loadJobLongList(jobOpp.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabName, jobOpp?.id]);

  const applyFiltersAndSort = useCallback(
    (items) => {
      if (!items) return [];

      let filtered = cloneDeep(items);

      if (!!filter.length) {
        filtered = items.filter((hit) => {
          let conditionValue;

          Object.keys(filters).forEach((filtersKey) => {
            const filtersByKey = filters[filtersKey].keys || {};
            Object.keys(filtersByKey).forEach((filterKey) => {
              if (filtersByKey[filterKey].value) {
                if (filters[filtersKey].filterCondition) {
                  conditionValue =
                    conditionValue && hit.match?.[filtersKey] === filterKey;
                } else {
                  conditionValue =
                    conditionValue || hit.match?.[filtersKey] === filterKey;
                }
              }
            });
          });

          return conditionValue;
        });
      }

      if (locationFilterEnabled) {
        filtered = filterUsersByLocation(
          filtered,
          jobOpp,
          locationFilterRadius
        );
      }

      if (sortOrder.key) {
        return filtered.sort((a, b) => {
          const valueA = accessNestedProperty(a, sortByKeyField[sortOrder.key]);
          const valueB = accessNestedProperty(b, sortByKeyField[sortOrder.key]);

          if (valueA == null && valueB == null) return 0;
          if (valueA == null) return 1;
          if (valueB == null) return -1;

          const result = sortComparator(a, b, sortOrder.key);

          return sortOrder.direction === SORTBYDIRECTION.DESCENDING
            ? -result
            : result;
        });
      }

      return filtered;
    },
    [
      filter.length,
      filters,
      sortOrder,
      locationFilterEnabled,
      locationFilterRadius,
      jobOpp,
    ]
  );

  const _mergeArrays = (array1 = [], array2 = []) => {
    const keyed1 = keyBy(array1, "id");
    const keyed2 = keyBy(array2, "id");

    const merged = merge({}, keyed1, keyed2);

    return values(merged).filter(({ id }) =>
      array2.some((hit) => hit.id === id)
    );
  };

  const mappedItemsObj = useMemo(() => {
    let finalItems = [];
    let sourceItems;

    switch (collectionKey) {
      // For mapHits are falling back on algolia hit record (hit.matches) for getting the associated match.
      // Since now when job is loaded not all matches are fetched.

      case "hits": {
        finalItems = mapHits(hits, matches, [], jobOpp.id);
        sourceItems = mapHits(hits, matches, [], jobOpp.id);
        break;
      }

      case "match": {
        finalItems = mapMatchesToHit(
          matches,
          matchStatus,
          activeTabName,
          excludedMatchStatuses
        );
        sourceItems = mapMatchesToHit(
          matches,
          matchStatus,
          activeTabName,
          excludedMatchStatuses
        );
        break;
      }

      case "longList": {
        finalItems = mapMatchesToHit(
          longList.items,
          null,
          activeTabName,
          excludedMatchStatuses
        );
        break;
      }

      default: {
        break;
      }
    }

    if (collectionKey === "match" || collectionKey === "longList") {
      finalItems = _mergeArrays(
        tabsMatchesRef.current[activeTabName]?.mappedTabItems,
        finalItems
      );
    }

    return { mappedTabItems: finalItems, sourceItems };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTabName,
    collectionKey,
    hits,
    matches,
    longList.items,
    matchStatus,
    excludedMatchStatuses,
    filterOutHitByMatchStatus,
    ideal,
    jobOpp.id,
  ]);

  if (tabsMatchesRef) {
    tabsMatchesRef.current[activeTabName] = mappedItemsObj;
  }

  const { mappedTabItems, sourceItems } = tabsMatchesRef
    ? tabsMatchesRef.current[activeTabName] ?? {
        mappedTabItems: [],
        sourceItems: [],
      }
    : mappedItemsObj;

  const finalItems = useMemo(() => {
    return applyFiltersAndSort(items);
  }, [items, applyFiltersAndSort]);

  useEffect(() => {
    if (collectionKey !== "longList") {
      return;
    }
    if (mappedTabItems.length === 0) {
      setLocationFilterEnabled(false);
    }
  }, [collectionKey, mappedTabItems.length]);

  const handleVisibleItemsChange = useCallback(
    (visibleItems = []) => {
      updateMappedTabHitsMatchesForDownload(visibleItems);
    },
    [updateMappedTabHitsMatchesForDownload]
  );

  useEffect(() => {
    if (finalItems.length === 0) {
      handleVisibleItemsChange([]);
    }
  }, [finalItems.length, handleVisibleItemsChange]);

  // Handle tab items change
  useEffect(() => {
    if (!isMatchTabForMatchAgent) {
      setItems(mappedTabItems);
      return;
    }

    const filteredItems = applyFiltersAndSort(mappedTabItems);

    if (filteredItems?.length === 0) {
      setItems(mappedTabItems);
      return;
    }

    const canReRank =
      isMatchTabForMatchAgent &&
      careerTrackAnalysisEnabled &&
      !isHandlingReRankRef.current;

    if (canReRank) {
      (async () => {
        isHandlingReRankRef.current = true;

        const hasRankDetails = filteredItems.every(
          (hit) => hit.rankDetails && !hit.rankDetails?.error
        );

        if (hasRankDetails) {
          isHandlingReRankRef.current = false;

          setItems(mappedTabItems);

          return;
        }

        handleIsReRankingMatchesDebounce(handleIsReRankingMatches, true);

        const hitsToReRank = filteredItems.filter(
          (hit) => !hit.rankDetails || hit.rankDetails?.error
        );

        const reRankedHits = await reRankHitsForJobs(
          jobOpp?.id,
          hitsToReRank.map(({ id, systemModTimestamp }) => ({
            id,
            systemModTimestamp,
          }))
        );

        if (!reRankedHits || reRankedHits.length === 0) {
          isHandlingReRankRef.current = false;
          handleIsReRankingMatchesDebounce(handleIsReRankingMatches, false);
          return;
        }

        tabsMatchesRef.current[activeTabName].mappedTabItems = mappedTabItems
          .map((hit) => {
            const { rankDetails } =
              reRankedHits.find(({ id }) => id === hit.id) ?? {};

            const newRankDetails = hit.rankDetails
              ? {
                  ...hit.rankDetails,
                  ...rankDetails,
                }
              : rankDetails;

            return {
              ...hit,
              rankDetails: newRankDetails,
            };
          })
          .filter((e) => e.id);

        isHandlingReRankRef.current = false;

        setItems(tabsMatchesRef.current[activeTabName].mappedTabItems);

        handleIsReRankingMatchesDebounce(handleIsReRankingMatches, false);
      })();
    } else {
      setItems(mappedTabItems);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mappedTabItems,
    careerTrackAnalysisEnabled,
    isMatchTabForMatchAgent,
    jobOpp?.id,
    filters,
  ]);

  // Calculate checksum of items with their status
  // to determine changes to items (and thus changes to status filters)
  const sourceChecksum = useMemo(() => {
    return generateFilterChecksum(sourceItems, filter);
  }, [filter, sourceItems]);

  useEffect(() => {
    if (!!filter.length) {
      const createdFilters = {};
      filter.forEach((f) => {
        createdFilters[f] = { keys: createFilter(sourceItems, f) };
        createdFilters[f].filterCondition = false;
      });

      // When setting or re-setting the filters, we need to take
      // any new filters while still keeping the state of the old filters
      // as long as the old filters are still a part of the new filters
      // Here, data structure of filter is:
      // {
      //   [string]: {
      //     filterCondition: boolean,
      //     keys: {
      //       label: string,
      //       value: boolean
      //     }[]
      //   }
      // }
      setFilters((prev) => {
        const finalFilters = {};

        Object.keys(createdFilters).forEach((outerKey) => {
          finalFilters[outerKey] = JSON.parse(
            JSON.stringify(createdFilters[outerKey])
          );

          if (finalFilters[outerKey].hasOwnProperty("keys")) {
            finalFilters[outerKey].keys = {};

            Object.keys(createdFilters[outerKey].keys).forEach((innerKey) => {
              if (
                prev[outerKey] &&
                prev[outerKey].keys &&
                prev[outerKey].keys.hasOwnProperty(innerKey)
              ) {
                finalFilters[outerKey].keys[innerKey] =
                  prev[outerKey].keys[innerKey];
              } else {
                finalFilters[outerKey].keys[innerKey] =
                  createdFilters[outerKey].keys[innerKey];
              }
            });
          }
        });

        return finalFilters;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!filter.length, sourceChecksum]);

  const handleSortOrderChange = (value) => {
    const [sortOrderKey, sortOrderDirection] = value.split("-");

    if (!sortOrderDirection) {
      setSortOrder({ key: null, direction: null });
    } else {
      setSortOrder({ key: sortOrderKey, direction: sortOrderDirection });
    }
  };

  const showResetButton = () => {
    if (activeTabName !== APPLICATION_TAB_NAMES.SHORTLISTED) return false;

    const show = finalItems.some(
      (item) => item.match?.status === "SHORTLISTED"
    );
    return show;
  };

  const handleResetButton = async () => {
    if (!finalItems) return;

    const confirmDeleting = window.confirm(
      "Are you sure you want to delete all shortlisted candidates?"
    );

    if (!confirmDeleting) return;

    const promises = finalItems.map(async (item) => {
      if (item.match?.status === "SHORTLISTED") {
        return await removeMatch(
          item.match?.applicationId,
          item.match?.jobOpportunityId
        );
      }
    });

    await Promise.all(promises);

    return;
  };

  const resultsFound = !!finalItems.length;
  const noResultBasicSearch =
    searchState.basicSearch && !searchState.customSearchQuery;
  const showViewModeControls =
    (activeTabSupportsTableView || activeTabSupportsMatchAgentView) &&
    resultsFound &&
    !searchState.basicSearch;

  useScrollToTopOnChange(searchState.page);

  useEffect(() => {
    if (!isLoadingAISearch || !isSearchResultsTab || !jobOpp?.id) return;

    const shuffled = [...SEARCH_AGENT_MESSAGES].sort(() => 0.5 - Math.random());
    let count = 0;

    setSearchAgentMessage(shuffled[count % shuffled.length]);

    const intervalId = setInterval(() => {
      count++;
      setSearchAgentMessage(shuffled[count % shuffled.length]);
    }, 2500);

    return () => {
      clearInterval(intervalId);
    };
  }, [isLoadingAISearch, isSearchResultsTab, jobOpp?.id]);

  const showMatchAgentLoader =
    !!jobOpp?.id &&
    !isLoadingAISearch &&
    (reRankHitsForJob?.isLoading || isReRankingMatches);

  useEffect(() => {
    if (!showMatchAgentLoader) return;

    const shuffled = [...MATCH_AGENT_MESSAGES].sort(() => 0.5 - Math.random());
    let count = 0;

    setMatchAgentMessage(shuffled[count % shuffled.length]);

    const intervalId = setInterval(() => {
      count++;
      setMatchAgentMessage(shuffled[count % shuffled.length]);
    }, 2500);

    return () => {
      clearInterval(intervalId);
    };
  }, [showMatchAgentLoader]);

  const getSortLabel = (label) => {
    if (
      label === "Matched Date" &&
      activeTabName === APPLICATION_TAB_NAMES.LONGLISTED
    ) {
      return "Date";
    }

    return label;
  };

  return (
    <div
      data-testid="candidate-results-container"
      className={cn("flex flex-col gap-4 h-full px-6 pt-2", {
        "bg-slate-100": resultsFound,
      })}
    >
      {isSearchResultsTab && jobOpp?.id && (
        <DivLoader
          loading={isLoadingAISearch}
          message={searchAgentMessage}
          shouldFadeOut
        >
          <div className="w-full bg-white">
            <JobSearchAgent />
          </div>
        </DivLoader>
      )}

      <div className="flex items-center gap-x-4 gap-y-3 flex-wrap">
        <div className="ml-auto flex justify-end gap-4 items-center flex-wrap">
          {(!isSearchResultsTab || allowSorting) && (
            <>
              {!!filter.length && (
                <Filters filters={filters} onChange={setFilters} />
              )}

              <div className="flex items-center gap-3">
                {showResetButton() && (
                  <div title="Reset Shortlist">
                    <RotateCcwIcon
                      label="Reset"
                      onClick={handleResetButton}
                      className=" cursor-pointer"
                    />
                  </div>
                )}

                <div className="flex-1 flex gap-2 items-center justify-end flex-wrap">
                  {allowSorting && (
                    <Select
                      value={`${sortOrder.key}-${sortOrder.direction}`}
                      onValueChange={(value) => handleSortOrderChange(value)}
                    >
                      <SelectTrigger value="">
                        <SelectValue placeholder="Sort By" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {Object.entries(sortByKey).map(([key, value]) =>
                            Object.entries(SORTBYDIRECTION).map(
                              ([dirKey, dirValue]) => (
                                <SelectItem
                                  value={`${value}-${dirValue}`}
                                  key={`${value}-${dirValue}`}
                                >
                                  {`${getSortLabel(value)} (${dirValue})`}
                                </SelectItem>
                              )
                            )
                          )}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {search && (
        <SearchBox
          translations={{
            placeholder: "Search for users",
          }}
          autoFocus
        />
      )}

      {collectionKey === "longList" && mappedTabItems.length > 0 && (
        <WebAppliesLocationFilter
          onFilterChange={(enabled, radius) => {
            setLocationFilterEnabled(enabled);
            setLocationFilterRadius(radius);
          }}
          isFiltering={locationFilterEnabled}
        />
      )}

      <div className="flex w-full flex-wrap items-center gap-x-8 gap-y-4">
        <div className="flex flex-wrap items-center gap-x-14 gap-y-4">
          {activeTabSupportsMatchAgentView &&
            resultsFound &&
            !searchState.basicSearch && (
              <div className="flex min-h-12 items-center">
                <CustomToggle
                  checked={careerTrackAnalysisEnabled}
                  rightLabel="Career track analysis"
                  helpText={CAREER_TRACK_ANALYSIS_TOOLTIP}
                  helpIconType="info"
                  helpIconClassName="text-slate-700"
                  helpIconSize={18}
                  size="medium"
                  className="text-sm font-normal text-slate-700"
                  labelClassName="leading-none"
                  toolTipClassName="!-translate-x-1/4"
                  onChange={handleCareerTrackAnalysisChange}
                />
              </div>
            )}

          {showViewModeControls && (
            <>
              <div
                id="view-type-group"
                className="flex h-12 items-center space-x-1 rounded-lg border border-slate-300 bg-white p-1 shadow-sm"
              >
                {activeTabSupportsMatchAgentView &&
                  careerTrackAnalysisEnabled && (
                    <button
                      id="view-type-match-agent"
                      className={cn(
                        {
                          "bg-slate-100 text-slate-950":
                            viewType === USER_LIST_VIEWS.MATCH_AGENT,
                          "hover:bg-slate-50":
                            viewType !== USER_LIST_VIEWS.MATCH_AGENT,
                        },
                        "flex h-10 w-fit cursor-pointer select-none items-center justify-center gap-3 whitespace-nowrap rounded-md px-4 text-sm font-normal text-slate-950 outline-none focus:bg-slate-100"
                      )}
                      onClick={() =>
                        handleViewTypeChange(USER_LIST_VIEWS.MATCH_AGENT)
                      }
                    >
                      <SvgIcon type="podium" className="h-[16]px" />
                      {USER_LIST_VIEW_LABELS[USER_LIST_VIEWS.MATCH_AGENT]}
                    </button>
                  )}

                <button
                  id="view-type-full-profile"
                  className={cn(
                    {
                      "bg-accent text-accent-foreground":
                        viewType === USER_LIST_VIEWS.FULL_PROFILE,
                    },
                    "w-fit justify-center flex gap-2 cursor-pointer select-none items-center rounded-sm px-3 py-1.5 text-sm font-medium outline-none focus:bg-accent focus:text-accent-foreground"
                  )}
                  onClick={() =>
                    handleViewTypeChange(USER_LIST_VIEWS.FULL_PROFILE)
                  }
                >
                  <UserIcon size={16} />
                  {USER_LIST_VIEW_LABELS[USER_LIST_VIEWS.FULL_PROFILE]}
                </button>

                {activeTabSupportsTableView && (
                  <button
                    id="view-type-list-view"
                    className={cn(
                      {
                        "bg-accent text-accent-foreground":
                          viewType === USER_LIST_VIEWS.LIST,
                      },
                      "w-fit justify-center flex gap-2 cursor-pointer select-none items-center rounded-sm px-3 py-1.5 text-sm font-medium outline-none focus:bg-accent focus:text-accent-foreground"
                    )}
                    onClick={() => handleViewTypeChange(USER_LIST_VIEWS.LIST)}
                  >
                    <ListIcon size={16} />
                    {USER_LIST_VIEW_LABELS[USER_LIST_VIEWS.LIST]}
                  </button>
                )}
              </div>

              {canAccessTalentCreation && (
                <>
                  <div className="hidden h-10 w-px bg-slate-200 sm:block" />
                  <TalentCreationModal
                    jobOpportunityId={jobOpp.id}
                    tooltip="Create New Talent"
                  />
                </>
              )}
            </>
          )}
        </div>

        {stats && (
          <div className="ml-auto justify-self-end">
            {!noResultBasicSearch && (
              <CustomStats
                reRankHitsForJob={
                  isSearchResultsTab ? reRankHitsForJob?.id : undefined
                }
                finalItems={finalItems}
                downloadResults={canDownloadSearchResults}
                downloadData={mappedHitsTabMatchesForDownload}
                useFinalItemsCount={!isSearchResultsTab}
              />
            )}
          </div>
        )}
      </div>

      <DivLoader loading={showMatchAgentLoader} message={matchAgentMessage}>
        <div
          className={cn({
            "min-h-[55vh]": showMatchAgentLoader,
          })}
        >
          {activeTabName === APPLICATION_TAB_NAMES.IDEAL && loadingIdeal ? (
            <DummyHits
              message={loadingIdeal}
              renderMessage={renderCustomNoResults}
            />
          ) : (
            <SearchGuard
              enabled={isSearchResultsTab && !finalItems.length}
              fallback={
                <div className="flex flex-col gap-4">
                  <DummyCard />
                  <DummyCard />
                  <DummyCard />
                </div>
              }
            >
              <UserListView
                items={finalItems}
                activeTabName={activeTabName}
                showActions={showActions}
                collectionKey={collectionKey}
                viewType={viewType}
                message={noResultsMessage}
                renderMessage={renderCustomNoResults}
                onVisibleItemsChange={handleVisibleItemsChange}
                allowedMatchAgentView={
                  activeTabSupportsMatchAgentView &&
                  careerTrackAnalysisEnabled &&
                  !searchState.basicSearch
                }
                showCareerTrackAnalysis={careerTrackAnalysisEnabled}
              />
            </SearchGuard>
          )}
          {pagination && !searchState.basicSearch && (
            <div className="my-8 flex justify-between">
              <Pagination />
              {showHitsPerPage && (
                <HitsPerPage
                  items={[
                    { value: 10, label: "Show 10 hits" },
                    { value: 25, label: "Show 25 hits" },
                  ]}
                  defaultRefinement={10}
                />
              )}
            </div>
          )}
        </div>
      </DivLoader>
    </div>
  );
};

const UserListView = ({
  items,
  activeTabName,
  showActions,
  collectionKey,
  viewType,
  message,
  renderMessage,
  onVisibleItemsChange,
  allowedMatchAgentView,
  showCareerTrackAnalysis,
}) => {
  const { jobOpp, loadJobTabMatches, longList } = useContext(JobsContext);
  const { searchState, loadingAISearch, reRankHitsForJob, pageIsLoaded } =
    useContext(SearchContext);

  const { user: loggedInUser } = useContext(AuthContext);

  const resultsFound = !!items.length;

  const isSearchResultsCollection =
    collectionKey === "hits" ||
    activeTabName === APPLICATION_TAB_NAMES.SEARCHRESULTS ||
    activeTabName === "SEARCHRESULTS";

  const isSearchLoading =
    !pageIsLoaded ||
    loadingAISearch === LOADING_STATES.LOADING ||
    reRankHitsForJob?.isLoading;

  const matchTabTokensStatus = activeTabName;

  const nexTokenStatusReq =
    jobOpp?.matchTabs?.[matchTabTokensStatus]?.nextTokensByStatus?.filter(
      (e) => e.nextToken
    ) || [];

  useEffect(() => {
    if (nexTokenStatusReq.length > 0) {
      (async () => {
        await loadJobTabMatches(
          jobOpp.id,
          nexTokenStatusReq,
          matchTabTokensStatus
        );
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabName]);

  if (
    jobOpp?.matchTabs?.[matchTabTokensStatus]?.isLoading ||
    longList.isLoading
  ) {
    return (
      <div className="flex justify-center pt-8">
        <span className="loader"></span>
      </div>
    );
  }

  if (searchState.basicSearch && !searchState.customSearchQuery) {
    return <></>;
  }

  if (isSearchResultsCollection && isSearchLoading) {
    return (
      <div className="flex justify-center pt-8">
        <span className="loader"></span>
      </div>
    );
  }

  const isSearchResultsTab =
    activeTabName === APPLICATION_TAB_NAMES.SEARCHRESULTS ||
    activeTabName === "SEARCHRESULTS";

  if ((!resultsFound || !jobOpp) && !isSearchLoading) {
    // we don't need to render the full message if basic search is enabled
    const dummyRenderMessage = searchState.basicSearch ? null : renderMessage;
    return <DummyHits message={message} renderMessage={dummyRenderMessage} />;
  }

  const activeTabSupportsTableView =
    loggedInUser?.canAccessListView && tabSupportsTableView(activeTabName);
  const allowTableView = activeTabSupportsTableView && !searchState.basicSearch;

  if (viewType === USER_LIST_VIEWS.MATCH_AGENT && allowedMatchAgentView) {
    return (
      <MatchAgentListView
        items={items}
        activeTabName={activeTabName}
        showActions={showActions}
        collectionKey={collectionKey}
        localPagination={!isSearchResultsTab}
        onVisibleItemsChange={onVisibleItemsChange}
      />
    );
  }

  if (!allowTableView || viewType === USER_LIST_VIEWS.FULL_PROFILE) {
    return (
      <UserCardListView
        items={items}
        activeTabName={activeTabName}
        showActions={showActions}
        collectionKey={collectionKey}
        onVisibleItemsChange={onVisibleItemsChange}
        showCareerTrackAnalysis={showCareerTrackAnalysis}
      />
    );
  }

  return (
    <UserTableListView
      items={items}
      activeTabName={activeTabName}
      showActions={showActions}
      collectionKey={collectionKey}
      onVisibleItemsChange={onVisibleItemsChange}
      showCareerTrackAnalysis={showCareerTrackAnalysis}
    />
  );
};

const UserCardListView = ({
  items,
  activeTabName,
  onVisibleItemsChange,
  ...rest
}) => {
  useEffect(() => {
    if (
      activeTabName === APPLICATION_TAB_NAMES.SEARCHRESULTS ||
      activeTabName === "SEARCHRESULTS"
    ) {
      onVisibleItemsChange(items);
    }
  }, [activeTabName, items, onVisibleItemsChange]);

  if (
    activeTabName === APPLICATION_TAB_NAMES.SEARCHRESULTS ||
    activeTabName === "SEARCHRESULTS"
  ) {
    return items.map((hit) => {
      const clonedHit = cloneDeep(hit);

      return (
        <UserCardResult
          key={clonedHit.id}
          hit={clonedHit}
          activeTabName={activeTabName}
          showActions={rest.showActions}
          collectionKey={rest.collectionKey}
          showCareerTrackAnalysis={rest.showCareerTrackAnalysis}
        />
      );
    });
  }

  return (
    <PaginatedList
      data={items}
      activeTabName={activeTabName}
      onVisibleItemsChange={onVisibleItemsChange}
      {...rest}
    />
  );
};

UserCardList.propTypes = {
  stats: PropTypes.bool,
  search: PropTypes.bool,
  pagination: PropTypes.bool,
  downloadResults: PropTypes.bool,
  collectionKey: PropTypes.string,
  noResultsMessage: PropTypes.string,
  activeTabName: PropTypes.string,
  renderCustomNoResults: PropTypes.object,
  showActions: PropTypes.bool,
  filter: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf([""])]),
  excludedMatchStatuses: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.oneOf([undefined]),
  ]),
  matchStatus: PropTypes.array,
  allowSorting: PropTypes.bool,
  showHitsPerPage: PropTypes.bool,
};

UserCardList.defaultProps = {
  stats: false,
  search: false,
  pagination: false,
  downloadResults: false,
  collectionKey: "hits",
  noResultsMessage: "No results to display",
  activeTabName: "SEARCHRESULTS",
  renderCustomNoResults: undefined,
  showActions: false,
  filter: [],
  excludedMatchStatuses: undefined,
  matchStatus: undefined,
  allowSorting: false,
  showHitsPerPage: false,
};

export default UserCardList;
